<template>
    <v-container fluid>
        <div v-if="!loading">
            <v-row>
                <v-col>
                    <h4 class="text-h4">{{$t("stock_adjustment")}} #{{data.code}}</h4>
                    <p class="text-subtitle-1">{{$t("used_to_adjustment_stock")}}</p>
                    <v-divider></v-divider>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="3" v-if="isPostable">
                    <v-btn color="primary" block :loading="postLoading" @click="onPost">
                        <v-icon left>mdi-post</v-icon>
                        {{$t("post_to_ledger")}}
                    </v-btn>
                </v-col>
                <v-col cols="12" md="3" v-if="isPrintable" :loading="printLoading" @click="onPrint">
                    <v-btn depressed block>
                        <v-icon left>mdi-printer</v-icon>
                        {{$t("print")}}
                    </v-btn>
                </v-col>
                <v-col cols="12" md="3" v-if="isEditable">
                    <v-btn depressed block @click="onEdit">
                        <v-icon left>mdi-pencil</v-icon>
                        {{$t("edit")}}
                    </v-btn>
                </v-col>
                <v-col cols="12" md="3" v-if="isDeletable">
                    <v-btn depressed block color="error--text" :loading="removeLoading" @click="onRemove">
                        <v-icon left>mdi-trash-can</v-icon>
                        {{$t("delete")}}
                    </v-btn>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>{{$t("code")}}</th>
                                <td>{{data.code}}</td>
                            </tr>
                            <tr>
                                <th>{{$t("document_date")}}</th>
                                <td>{{data.date}}</td>
                            </tr>
                            <tr>
                                <th>{{$t("warehouse")}}</th>
                                <td>
                                    <span v-if="data && data.warehouse">
                                        {{data.warehouse.display_name}}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <th>{{$t("reference")}}</th>
                                <td>{{data.reference}}</td>
                            </tr>
                            <tr>
                                <th>{{$t("state")}}</th>
                                <td>
                                    <PStatus :state="data.state"/>
                                </td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
            </v-row>
            <v-row>
                <v-col>
                    <v-simple-table dense>
                        <thead>
                            <tr>
                                <th>#</th>
                                <th>{{$t("item")}}</th>
                                <th>{{$t("quantity")}}</th>
                                <th>{{$t("measure_unit")}}</th>
                                <th>{{$t("unit_value")}}</th>
                                <th>{{$t("total_value")}}</th>
                                <th>{{$t("account")}}</th>
                                <th>{{$t("dimension")}}</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="(line, index) in data.lines" :key="index">
                                <td>{{ index + 1 }}</td>
                                <td>{{ line.item.display_name }}</td>
                                <td>{{ formatNumber(line.quantity) }}</td>
                                <td>{{ line.measure_unit.abbreviation }}</td>
                                <td>{{ formatNumber(line.value / line.quantity) }}</td>
                                <td>{{ formatNumber(line.value) }}</td>
                                <td>{{ line.account.display_name }}</td>
                                <td>{{ line.dimension ? line.dimension.name : "--" }}</td>
                            </tr>
                        </tbody>
                        <tfoot>
                            <tr class="font-weight-bold">
                                <td></td>
                                <td>{{$t("total")}}</td>
                                <td>{{formatNumber(getTotal(data.lines, "quantity"))}}</td>
                                <td></td>
                                <td></td>
                                <td>{{formatNumber(getTotal(data.lines, "value"))}}</td>
                                <td></td>
                                <td></td>
                            </tr>
                        </tfoot>
                    </v-simple-table>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>{{$t("remarks")}}</th>
                                <td>{{data.remarks}}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="4">
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>{{$t("created_by")}}</th>
                                <td>
                                    <span v-if="data.created_by">
                                        {{data.created_by.first_name}} {{data.created_by.last_name}}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <th>{{$t("created_at")}}</th>
                                <td>{{data.created_at}}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
                <v-col cols="12" md="4">
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>{{$t("updated_by")}}</th>
                                <td>
                                    <span v-if="data.updated_by">
                                        {{data.updated_by.first_name}} {{data.updated_by.last_name}}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <th>{{$t("updated_at")}}</th>
                                <td>{{data.updated_at}}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
                <v-col cols="12" md="4">
                    <v-simple-table dense>
                        <tbody>
                            <tr>
                                <th>{{$t("posted_by")}}</th>
                                <td>
                                    <span v-if="data.posted_by">
                                        {{data.posted_by.first_name}} {{data.posted_by.last_name}}
                                    </span>
                                </td>
                            </tr>
                            <tr>
                                <th>{{$t("posted_at")}}</th>
                                <td>{{data.posted_at}}</td>
                            </tr>
                        </tbody>
                    </v-simple-table>
                </v-col>
            </v-row>
        </div>
        <div v-else>
            <v-row>
                <v-col>
                    <v-skeleton-loader type="heading" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-divider></v-divider>
                </v-col>
            </v-row>
            <v-row>
                <v-col class="d-flex">
                    <v-skeleton-loader type="button" class="my-4 me-4"/>
                    <v-skeleton-loader type="button" class="my-4"/>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                    <v-skeleton-loader type="text" class="my-4"/>
                </v-col>
            </v-row>
            <v-row no-gutters>
                <v-col>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                </v-col>
            </v-row>
            <v-row>
                <v-col cols="12" md="6">
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                </v-col>
                <v-col cols="12" md="6">
                    <v-skeleton-loader type="text" class="my-2"/>
                    <v-skeleton-loader type="text" class="my-2"/>
                </v-col>
            </v-row>
        </div>
        <PAlert v-model="alert.value" v-bind="alert"/>
    </v-container>
</template>

<script>
import stockAdjustment from './../../services/stockJournal/stockAdjustment';
import PAlert from './../../components/common/PAlert.vue';
import PStatus from '../../components/common/PStatus.vue';
import { getTotal } from '../../utils/accounting';
import { formatNumber } from 'accounting';
import authorize from './../../utils/authorize';
import permissions from './../../data/permissions';

export default {
    components: {
        PAlert,
        PStatus
    },
    data() {
        return {
            data: {},
            loading: false,
            removeLoading: false,
            printLoading: false,
            postLoading: false,
            id: null,
            alert: {
                content: "Are You sure?",
                value: false,
                callback: null   
            },
            permissions,
            warehouseId: null
        }
    },
    computed: {
        isEditable() {
            return this.data && this.data.meta && this.data.meta.is_editable && this.hasPermission(this.permissions.stock_adjustment.update);
        },
        isDeletable() {
            return this.data && this.data.meta && this.data.meta.is_deletable && this.hasPermission(this.permissions.stock_adjustment.delete);
        },
        isPostable() {
            return this.data && this.data.meta && this.data.meta.is_postable && this.hasPermission(this.permissions.stock_adjustment.post);
        },
        isPrintable() {
            return this.data && this.data.meta && this.data.meta.is_printable && this.hasPermission(this.permissions.stock_adjustment.print);
        }
    },
    methods: {
        getTotal,
        formatNumber(n) {
            return formatNumber(n, 2);
        },
        fetch() {
            this.loading = true;
            stockAdjustment.get(this.id).then(res => {
                this.data = res.data;
            }).finally(() => {
                this.loading = false;
            })
        },
        onRemove() {
            this.alert = {...this.alert, value: true, content: this.$t("are_you_sure_remove"), callback: this.remove};
        },
        remove() {
            this.removeLoading = true;
            stockAdjustment.remove(this.id).then(() => {
                this.$router.replace({
                    name: "AdjustmentsList"
                });
            }).finally(() => {
                this.removeLoading = false;
            })
        },
        onPrint() {
            this.printLoading = true;
            stockAdjustment.print(this.id)
                .then(response => {
                    var printWindow = window.open('','','width=800,height=600');
                    printWindow.document.write(response.data);
                    printWindow.document.close();
                    printWindow.focus();
                    printWindow.print();
                    printWindow.close();
                })
                .finally(() => {
                    this.printLoading = false;
                })
        },
        onPost() {
            this.alert = {...this.alert, value: true, content: this.$t("are_you_sure_post"), callback: this.post};
        },
        post() {
            this.postLoading = true;
            stockAdjustment.post(this.id).then((res) => {
                this.data = res.data;
            }).finally(() => {
                this.postLoading = false;
                this.fetch()
            })
        },
        onEdit() {
            this.$router.push({
                name: "AdjustmentEdit",
                params: {
                    id: this.id
                }
            })
        },
        hasPermission(code) {
            return authorize(code);
        }
    },
    created() {
        this.id = this.$route.params.id;
        this.warehouseId = this.$route.params.warehouseId;
        this.fetch();
    }
}
</script>

<style>

</style>